import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  BARE_DOMAIN_REGEX,
  type CustomerCreatedResponse,
  DATE_FORMAT_OPTIONS,
  DateFormat,
  GOOGLE_CUSTOMER_ID_REGEX,
  TIMEZONE_OPTIONS,
  Timezone,
} from '@cdw-ae/common-data';
import { CdwButtonModule } from '@cdw-ae/common-ui-components-button';
import { CdwCheckboxModule } from '@cdw-ae/common-ui-components-checkbox';
import { CdwIconModule } from '@cdw-ae/common-ui-components-icon';
import { CdwSelectModule } from '@cdw-ae/common-ui-components-select';
import { CommonUiComponentsTextInputModule } from '@cdw-ae/common-ui-components-text-input';
import { PlatformCustomerService } from '../../../../common/services/platform-customer/platform-customer.service';

interface SelectOption {
  value: string;
  displayName: string;
}

@Component({
  selector: 'cdw-customer-onboarding',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    CdwButtonModule,
    CdwCheckboxModule,
    CdwSelectModule,
    CommonUiComponentsTextInputModule,
    CdwIconModule,
  ],
  templateUrl: './customer-onboarding.component.html',
  styleUrl: './customer-onboarding.component.scss',
})
export class CustomerOnboardingComponent {
  private readonly fb = inject(FormBuilder);
  private readonly customerService = inject(PlatformCustomerService);

  readonly timezoneOptions: SelectOption[] = TIMEZONE_OPTIONS.map((o) => ({
    value: o.value,
    displayName: o.name,
  }));
  readonly dateFormatOptions: SelectOption[] = DATE_FORMAT_OPTIONS.map((o) => ({
    value: o.format,
    displayName: o.displayName,
  }));

  readonly form = this.fb.group({
    name: ['', [Validators.required]],
    primaryDomain: [
      '',
      // Bare domain only — no protocol, no spaces (server re-validates).
      [Validators.required, Validators.pattern(BARE_DOMAIN_REGEX)],
    ],
    googleId: [
      '',
      // Google customer id, e.g. C02c9bmnt (server re-validates).
      [Validators.required, Validators.pattern(GOOGLE_CUSTOMER_ID_REGEX)],
    ],
    delegateEmail: ['', [Validators.email]],
    schoolDistrict: [''],
    timezone: [Timezone.EASTERN as string],
    dateFormat: [DateFormat.US as string],
  });

  isSubmitting = false;
  errorMessage: string | null = null;
  created: CustomerCreatedResponse | null = null;
  /**
   * Set when submit is attempted with a blank delegate. Support must tick the
   * confirmation to proceed — a customer with no delegate can't pass DWD or
   * sign in (a later self-service onboarding flow will let customers set it).
   */
  confirmNoDelegate = false;

  get nameControl() {
    return this.form.controls.name;
  }
  get primaryDomainControl() {
    return this.form.controls.primaryDomain;
  }
  get googleIdControl() {
    return this.form.controls.googleId;
  }

  /** No delegate entered — support must confirm before creating. */
  get delegateMissing(): boolean {
    return !this.form.controls.delegateEmail.value?.trim();
  }

  submit(): void {
    if (this.isSubmitting) {
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    // Gate: a blank delegate needs an explicit confirmation from support.
    if (this.delegateMissing && !this.confirmNoDelegate) {
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = null;
    this.created = null;

    const raw = this.form.getRawValue();
    const delegateEmail = raw.delegateEmail?.trim() || undefined;
    this.customerService
      .createCustomer({
        name: (raw.name ?? '').trim(),
        primaryDomain: (raw.primaryDomain ?? '').trim().toLowerCase(),
        googleId: (raw.googleId ?? '').trim(),
        delegateEmail,
        schoolDistrict: raw.schoolDistrict?.trim() || undefined,
        timezone: raw.timezone || undefined,
        dateFormat: raw.dateFormat || undefined,
      })
      .subscribe({
        next: (customer) => {
          this.created = customer;
          this.isSubmitting = false;
          this.confirmNoDelegate = false;
          this.form.reset({
            timezone: Timezone.EASTERN,
            dateFormat: DateFormat.US,
          });
        },
        error: (err: HttpErrorResponse) => {
          this.errorMessage = this.extractError(err);
          this.isSubmitting = false;
        },
      });
  }

  onboardAnother(): void {
    this.created = null;
    this.errorMessage = null;
  }

  private extractError(err: HttpErrorResponse): string {
    const backend = (err.error as { message?: string | string[] } | null)
      ?.message;
    if (Array.isArray(backend) && backend.length > 0) {
      return backend.join(' ');
    }
    if (typeof backend === 'string' && backend.length > 0) {
      return backend;
    }
    if (err.status === 403) {
      return 'You are not authorized to onboard customers.';
    }
    return 'Something went wrong creating the customer. Please try again.';
  }
}
